import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { EmailOtpType, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { consumeOAuthReferral, ensureNativeOAuthListener } from '@/features/auth/googleAuth';
import { consumePasswordRecoveryPending } from '@/features/auth/passwordRecovery';
import { findReferrerProfileId } from '@/features/auth/referral';
import '@/pages/marketing/marketing.css';

const OTP_TYPES = new Set<string>([
  'signup',
  'invite',
  'magiclink',
  'recovery',
  'email_change',
  'email',
]);

/** Évite un double exchangeCodeForSession (StrictMode / remount) qui consomme le code PKCE. */
let pendingCodeExchange: { code: string; promise: Promise<Session | null> } | null = null;

function clearAuthParamsFromUrl() {
  const url = new URL(window.location.href);
  url.search = '';
  url.hash = '';
  window.history.replaceState({}, document.title, url.pathname);
}

async function resolveSessionFromHash(): Promise<Session | null> {
  const hash = window.location.hash?.replace(/^#/, '') ?? '';
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const access_token = params.get('access_token');
  const refresh_token = params.get('refresh_token');
  if (!access_token || !refresh_token) return null;
  const { data, error } = await supabase.auth.setSession({ access_token, refresh_token });
  if (error) throw error;
  return data.session;
}

async function exchangeCodeOnce(code: string): Promise<Session | null> {
  if (pendingCodeExchange?.code === code) {
    return pendingCodeExchange.promise;
  }

  const promise = (async () => {
    const { data: existing } = await supabase.auth.getSession();
    if (existing.session) return existing.session;

    const { data, error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) {
      const { data: retry } = await supabase.auth.getSession();
      if (retry.session) return retry.session;
      throw error;
    }
    return data.session ?? null;
  })();

  pendingCodeExchange = { code, promise };
  try {
    return await promise;
  } finally {
    if (pendingCodeExchange?.promise === promise) {
      pendingCodeExchange = null;
    }
  }
}

/**
 * Finalise OAuth Google et les liens e-mail (confirmation, reset, magic link).
 * - token_hash + type → verifyOtp (fonctionne d’un appareil / navigateur à l’autre)
 * - code → exchangeCodeForSession (PKCE, même navigateur que le démarrage du flux)
 * - #access_token → setSession (flux implicite legacy)
 */
async function resolveSession(): Promise<Session | null> {
  const url = new URL(window.location.href);
  const tokenHash = url.searchParams.get('token_hash');
  const typeParam = url.searchParams.get('type');

  if (tokenHash && typeParam && OTP_TYPES.has(typeParam)) {
    const { data, error } = await supabase.auth.verifyOtp({
      token_hash: tokenHash,
      type: typeParam as EmailOtpType,
    });
    if (error) throw error;
    if (data.session) return data.session;
  }

  const code = url.searchParams.get('code');
  if (code) {
    const session = await exchangeCodeOnce(code);
    if (session) return session;
  }

  const fromHash = await resolveSessionFromHash();
  if (fromHash) return fromHash;

  for (let i = 0; i < 10; i++) {
    const { data } = await supabase.auth.getSession();
    if (data.session) return data.session;
    await new Promise((r) => setTimeout(r, 150));
  }
  return null;
}

function isOAuthCallbackUrl(): boolean {
  const url = new URL(window.location.href);
  return Boolean(url.searchParams.get('code')) && !url.searchParams.get('token_hash');
}

function friendlyAuthError(message: string): string {
  const pkceMismatch =
    /PKCE code verifier/i.test(message) ||
    /pkce_code_verifier/i.test(message) ||
    /code challenge does not match/i.test(message);
  if (pkceMismatch) {
    if (isOAuthCallbackUrl()) {
      return (
        'La connexion Google a été interrompue (session de sécurité expirée). ' +
        'Réessayez depuis la page de connexion de l’application.'
      );
    }
    return (
      'Ce lien de confirmation n’est plus valide dans ce navigateur (sécurité PKCE). ' +
      'Demandez un nouvel e-mail de confirmation, ou mettez à jour le template Supabase ' +
      'avec le lien token_hash (docs/email-templates/confirm-signup.html).'
    );
  }
  if (/otp_expired|token.*expired|expired|invalid/i.test(message)) {
    return (
      'Ce lien a expiré ou a déjà été utilisé. Demandez un nouvel e-mail depuis l’inscription ou « Mot de passe oublié ».'
    );
  }
  return message;
}

/**
 * Point de retour OAuth (Google) et confirmation e-mail.
 * Échange le code / token puis route selon le rôle.
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    ensureNativeOAuthListener();
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const url = new URL(window.location.href);
        const oauthError =
          url.searchParams.get('error_description') || url.searchParams.get('error');
        if (oauthError) {
          if (alive) setError(friendlyAuthError(oauthError));
          return;
        }

        const session = await resolveSession();
        if (!alive) return;

        if (!session?.user) {
          setError('Connexion interrompue. Réessayez depuis la page de connexion.');
          return;
        }

        const isRecovery =
          consumePasswordRecoveryPending() ||
          url.searchParams.get('type') === 'recovery' ||
          url.searchParams.get('flow') === 'recovery' ||
          /(?:^|&)type=recovery(?:&|$)/i.test(window.location.hash.replace(/^#/, ''));

        clearAuthParamsFromUrl();

        if (isRecovery) {
          navigate('/reinitialiser-mot-de-passe', { replace: true });
          return;
        }

        const referral = consumeOAuthReferral();
        if (referral) {
          const parrainId = await findReferrerProfileId(referral);
          if (parrainId && parrainId !== session.user.id) {
            await supabase
              .from('profiles')
              .update({ referred_by: parrainId })
              .eq('id', session.user.id)
              .is('referred_by', null);
          }
        }

        // Attendre que le trigger handle_new_user ait créé le profil
        let role: string | undefined;
        for (let i = 0; i < 10; i++) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', session.user.id)
            .maybeSingle();
          if (profile) {
            role = profile.role;
            break;
          }
          await new Promise((r) => setTimeout(r, 200));
        }

        if (!alive) return;
        navigate(role === 'admin' || role === 'advisor' ? '/admin' : '/app', { replace: true });
      } catch (err) {
        if (!alive) return;
        setError(
          friendlyAuthError(err instanceof Error ? err.message : 'Échec de la finalisation de connexion'),
        );
      }
    })();

    return () => {
      alive = false;
    };
  }, [navigate]);

  if (error) {
    return (
      <div className="page-center">
        <h1>Connexion</h1>
        <p className="error-banner">{error}</p>
        <p className="stack-gap">
          <Link className="btn btn-primary" to="/connexion">
            Retour à la connexion
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="page-center">
      <p className="muted">Finalisation de la connexion…</p>
    </div>
  );
}
